/**
 * Audio Conductor — reads game state and drives music stem mix.
 *
 * Called at ~10Hz from the game loop. Computes target gains for each music
 * stem based on turn phase, combat intensity, player health, build mode,
 * match phase, and idle time. Applies gains via MusicManager.setStemGain().
 *
 * Rules follow AUDIO_ARCHITECTURE.md Section 3.
 */

import type { MusicManager, MusicStemId } from './music';
import { stemRampSeconds } from './musicMix';

// ---------------------------------------------------------------------------
// Conductor input state — computed by main.ts each tick
// ---------------------------------------------------------------------------

export type ConductorTurnPhase =
  | 'idle'               // waiting for bot, between actions
  | 'aiming'             // human player actively aiming
  | 'firing'             // projectile launch moment
  | 'projectileInFlight' // projectile traversing
  | 'resolving'          // impacts / damage happening
  | 'building';          // player in build mode

export type ConductorMatchPhase = 'early' | 'mid' | 'endgame';

export interface ConductorState {
  turnPhase: ConductorTurnPhase;
  combatIntensity: number;      // 0-1, decayed per frame, spiked on events
  playerHealth: number;          // 0-1 ratio of current human player
  buildMode: boolean;
  matchPhase: ConductorMatchPhase;
  timeSinceLastAction: number;   // seconds since last player action
}

// ---------------------------------------------------------------------------
// Mix targets — the gains the conductor wants for each stem
// ---------------------------------------------------------------------------

interface MixTargets {
  calm: number;
  tension: number;
  combat: number;
  lowhealth: number;
  endgame: number;
}

// Per-stem ramp durations based on transition direction (audio/musicMix.ts)
function getRampDuration(stem: MusicStemId, prev: number, next: number): number {
  return stemRampSeconds(stem, prev, next);
}

// ---------------------------------------------------------------------------
// Core mix computation
// ---------------------------------------------------------------------------

function computeMixTargets(state: ConductorState): MixTargets {
  let calm = 0;
  let tension = 0;
  let combat = 0;
  let lowhealth = 0;
  let endgame = 0;

  // 1. Base: calm
  calm = 1.0;

  // 2. Turn phase adjustments
  switch (state.turnPhase) {
    case 'aiming':
      tension = 0.6;
      calm = 0.4;
      break;
    case 'firing':
    case 'projectileInFlight':
      tension = 1.0;
      calm = 0;
      break;
    case 'resolving':
      tension = 0.8;
      calm = 0.1;
      break;
    case 'building':
      // Build mode: stay calm, reduce tension
      calm = 1.0;
      tension = 0.1;
      break;
    case 'idle':
    default:
      // calm stays at 1.0
      break;
  }

  // 3. Combat intensity overlay (overrides tension when high)
  if (state.combatIntensity > 0.3) {
    const ci = state.combatIntensity;
    combat = ci;
    // Blend tension down as combat rises
    tension = Math.max(tension * (1 - ci), 0);
    // Also pull calm down during combat
    calm = Math.max(calm * (1 - ci * 0.8), 0);
  }

  // 4. Low health overlay (additive, doesn't replace other stems)
  if (state.playerHealth < 0.3 && state.playerHealth > 0) {
    lowhealth = (0.3 - state.playerHealth) / 0.3;
  }

  // 5. Endgame override
  if (state.matchPhase === 'endgame') {
    endgame = 0.8;
    // Reduce everything else proportionally
    const dampen = 0.25;
    calm *= dampen;
    tension *= dampen;
    combat *= dampen;
  }

  // 6. Build mode — restore calm, suppress tension/combat
  if (state.buildMode) {
    calm = Math.max(calm, 0.8);
    tension *= 0.3;
    combat *= 0.3;
  }

  // 7. Idle fade — if no action for 10+ seconds, gradually pull everything down
  if (state.timeSinceLastAction > 10) {
    const fade = Math.max(0.3, 1 - (state.timeSinceLastAction - 10) / 20);
    calm *= fade;
    tension *= fade;
    combat *= fade;
    endgame *= fade;
    // lowhealth stays — heartbeat shouldn't fade just because idle
  }

  return { calm, tension, combat, lowhealth, endgame };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Previous targets — only update stems when targets change significantly. */
let prevTargets: MixTargets = { calm: 0, tension: 0, combat: 0, lowhealth: 0, endgame: 0 };
const THRESHOLD = 0.03;

export function updateMusicMix(state: ConductorState, music: MusicManager): void {
  const targets = computeMixTargets(state);

  const stems: MusicStemId[] = ['calm', 'tension', 'combat', 'lowhealth', 'endgame'];
  for (const stem of stems) {
    const diff = Math.abs(targets[stem] - prevTargets[stem]);
    if (diff > THRESHOLD) {
      const ramp = getRampDuration(stem, prevTargets[stem], targets[stem]);
      music.setStemGain(stem, targets[stem], ramp);
    }
  }

  prevTargets = targets;
}

/** Reset conductor state (call on game start/restart). */
export function resetConductor(): void {
  prevTargets = { calm: 0, tension: 0, combat: 0, lowhealth: 0, endgame: 0 };
}
